import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Chip, Input, Select } from "../ui";
import type { PropDef, PropVal } from "../core/schema";
import { propLabel } from "./propLayout";

const toggle =
  "h-[24px] rounded-sm border px-2 text-[12.5px] font-medium transition aria-pressed:border-accent aria-pressed:bg-accent-tint aria-pressed:text-accent border-neutral-200 text-neutral-600 hover:bg-neutral-100 disabled:opacity-60 dark:border-neutral-800 dark:text-neutral-300 dark:hover:bg-neutral-800";

/** Label above a 30 px control, both small: the dense variant of `Field` for the task panel. */
function Slot({ label, htmlFor, children }: { label: string; htmlFor?: string; children: ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <label htmlFor={htmlFor} className="truncate text-[11.5px] text-neutral-500">
        {label}
      </label>
      {children}
    </div>
  );
}

/** One control per PropDef kind (spec §9.4), compact. Value `null` clears the prop. A tag is a toggle
 * chip, meant to sit in a row with the other tags. `fillDefault`: a number not set shows the definition's
 * default (what counts) instead of an empty field; the default is not written until the person changes it. */
export function PropEditor({
  def,
  value,
  onChange,
  disabled,
  fillDefault = false,
}: {
  def: PropDef;
  value: PropVal | undefined;
  onChange(v: PropVal | null): void;
  disabled?: boolean;
  fillDefault?: boolean;
}) {
  const { t } = useTranslation();
  // what is being typed: emptying the field to type a new number does not bring the default back at once
  const [draft, setDraft] = useState<string | null>(null);
  const label = propLabel(def, t);
  const id = `prop-${def.id}`;
  switch (def.kind) {
    case "tag":
      return (
        <button
          type="button"
          aria-pressed={value === true}
          disabled={disabled}
          onClick={() => onChange(value === true ? null : true)}
          className={toggle}
          data-testid={`tag-${def.id}`}
        >
          {label}
        </button>
      );
    case "number": {
      const spec = def.spec as {
        min?: number;
        max?: number;
        step?: number;
        unit?: string;
        default?: unknown;
      };
      const shown =
        typeof value === "number"
          ? String(value)
          : fillDefault && typeof spec.default === "number"
            ? String(spec.default)
            : "";
      return (
        <Slot label={spec.unit ? `${label} (${spec.unit})` : label} htmlFor={id}>
          <Input
            compact
            id={id}
            type="number"
            inputMode="decimal"
            min={spec.min}
            max={spec.max}
            step={spec.step ?? "any"}
            value={draft ?? shown}
            disabled={disabled}
            onChange={(e) => {
              setDraft(e.target.value);
              onChange(e.target.value === "" ? null : Number(e.target.value));
            }}
            onBlur={() => setDraft(null)}
            data-default={typeof value !== "number" && shown !== "" ? "true" : undefined}
          />
        </Slot>
      );
    }
    case "text":
      return (
        <Slot label={label} htmlFor={id}>
          <Input
            compact
            id={id}
            value={typeof value === "string" ? value : ""}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || null)}
          />
        </Slot>
      );
    case "datetime": {
      const local = typeof value === "string" && value ? toLocalInput(value) : "";
      return (
        <Slot label={label} htmlFor={id}>
          <Input
            compact
            id={id}
            type="datetime-local"
            value={local}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value ? new Date(e.target.value).toISOString() : null)}
          />
        </Slot>
      );
    }
    case "enum": {
      const spec = def.spec as { values: { id: string; label: string }[]; multi?: boolean; default?: string };
      if (spec.multi) {
        const cur = Array.isArray(value) ? value : [];
        return (
          <Slot label={label}>
            <div className="flex flex-wrap gap-1" role="group" aria-label={label}>
              {spec.values.map((v) => {
                const on = cur.includes(v.id);
                return (
                  <button
                    key={v.id}
                    type="button"
                    aria-pressed={on}
                    disabled={disabled}
                    onClick={() => {
                      const next = on ? cur.filter((x) => x !== v.id) : [...cur, v.id];
                      onChange(next.length ? next : null);
                    }}
                    className={toggle}
                  >
                    {t(`enum.${def.id}.${v.id}`, { defaultValue: v.label })}
                  </button>
                );
              })}
            </div>
          </Slot>
        );
      }
      return (
        <Slot label={label} htmlFor={id}>
          <Select
            compact
            id={id}
            className="w-full"
            value={typeof value === "string" ? value : (spec.default ?? "")}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value || null)}
          >
            {!spec.default && <option value="">—</option>}
            {spec.values.map((v) => (
              <option key={v.id} value={v.id}>
                {t(`enum.${def.id}.${v.id}`, { defaultValue: v.label })}
              </option>
            ))}
          </Select>
        </Slot>
      );
    }
    case "geo": {
      const g = value && typeof value === "object" && !Array.isArray(value) ? value : { lat: "", lon: "" };
      return (
        <Slot label={label}>
          <div className="flex items-center gap-2">
            <Input
              compact
              type="number"
              step="any"
              aria-label={`${label} lat`}
              placeholder="lat"
              value={g.lat}
              disabled={disabled}
              onChange={(e) =>
                onChange(
                  e.target.value === "" ? null : { lat: Number(e.target.value), lon: Number(g.lon) || 0 },
                )
              }
            />
            <Input
              compact
              type="number"
              step="any"
              aria-label={`${label} lon`}
              placeholder="lon"
              value={g.lon}
              disabled={disabled}
              onChange={(e) =>
                onChange(
                  e.target.value === "" ? null : { lat: Number(g.lat) || 0, lon: Number(e.target.value) },
                )
              }
            />
            {value && typeof value === "object" && !Array.isArray(value) && (
              <Chip onRemove={() => onChange(null)} removeLabel={t("common.remove")}>
                {t("common.remove")}
              </Chip>
            )}
          </div>
        </Slot>
      );
    }
  }
}

/** A mirrored value as text; a web address becomes a link. */
export function ReadOnlyValue({ def, value }: { def: PropDef; value: PropVal }) {
  const { i18n } = useTranslation();
  if (typeof value === "string" && /^https?:\/\//.test(value))
    return (
      <a href={value} target="_blank" rel="noreferrer" className="truncate text-accent hover:underline">
        {value.replace(/^https?:\/\//, "")}
      </a>
    );
  if (def.kind === "datetime" && typeof value === "string") {
    const d = new Date(value);
    if (!Number.isNaN(d.getTime()))
      return <span>{d.toLocaleString(i18n.language, { dateStyle: "medium", timeStyle: "short" })}</span>;
  }
  if (Array.isArray(value)) return <span>{value.join(", ")}</span>;
  if (typeof value === "object") return <span>{`${value.lat}, ${value.lon}`}</span>;
  return <span className="truncate">{String(value)}</span>;
}

function toLocalInput(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}
